import {Component, ElementRef, Input, OnDestroy, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {combineLatest, Observable, Subscription} from "rxjs";

import {faTrashAlt} from '@fortawesome/free-regular-svg-icons';
import {faLevelUpAlt, faList, faPlus} from '@fortawesome/free-solid-svg-icons';
import {DocumentService} from "../../services/document.service";
import {NavigationEnd, Router} from "@angular/router";
import {filter, first} from "rxjs/operators";
import {Node, NodeType} from "../../models/document/node";
import {BackResourceService} from "../../services/back-resource.service";
import {Breadcrumb} from "../../models/breadcrumb";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {LogementService} from "../../services/logement.service";
import {ToastService} from "../../services/toast.service";
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {Tree} from "../../models/document/tree";
import {Logement} from "../../models/logement";
import {UserService} from "../../services/user.service";
import {UserDocument} from "../../models/userDocument";
import { HttpClient } from "@angular/common/http";
import {faCog, faShareAlt, faFolder} from '@fortawesome/free-solid-svg-icons';
import {Deserialize} from "cerialize";
import {DocumentSharingHistory} from "../../models/documentSharingHistory";
import {formatDate} from "@angular/common";
import {FormHelperService} from "../../services/form-helper.service";
import {documentObligatoire} from "../../models/document/documentObligatoire";

@Component({
    selector: 'app-documents',
    templateUrl: './documents.component.html',
    styleUrls: ['./documents.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DocumentsComponent implements OnInit, OnDestroy {

  NodeType = NodeType;
  faCog = faCog;
  faList = faList;
  faShareAlt = faShareAlt;
  faFolder = faFolder;
  documentSharings: any;
  faLevelUpAlt = faLevelUpAlt;
  faPlus = faPlus;
  faTrashAlt = faTrashAlt;

  logement$: Observable<Logement> = null;
  usersDocuments$: Observable<UserDocument[]> = null;
  tree$: Observable<Tree> = null;
  currentNode: Node = null;
  moveTargetCurrentNode: Node = null;
  breadcrumbs: Breadcrumb[] = null;

  files: File[] = [];
  filesData: FormData;
  uploadProgress = null;
  @ViewChild('uploadModal')
  uploadModal: ElementRef;
  hasFileTooLarge = false;
  hasFileExtentionNotSupported = false;
  moveRootNodes: Node[] = [];
  moveTargetCurrentNodeFiltered: Node = null;
  isMoveRootFiltered = true;
  sourceFolderId: number = null;


  private subscription: Subscription;
  private uploadSubscription: Subscription;

  private readonly DEFAULT_NOM = 'Dossier sans titre';


  form = this.fb.group({
    id: [],
    parentId: [],
    type: [],
    nom: [this.DEFAULT_NOM]
  });
  private documentObligatoire: documentObligatoire[];
  activeDocumentObligatoire: number = null;
  activeDocumentId: number;

  private fh: FormHelperService;
  private data: any;
  public formSharing: UntypedFormGroup;

  constructor(
    private router: Router,
    public logementService: LogementService,
    private fb: UntypedFormBuilder,
    private documentService: DocumentService,
    private backResourceService: BackResourceService,
    public modalService: NgbModal,
    private toastService: ToastService,
    public userService: UserService,
    private http: HttpClient
  ) {
    this.logement$ = logementService.logementCourant;
    this.loadTree();
  }

  private loadTree() {
    const router$ = this.router.events.pipe(filter(event => event instanceof NavigationEnd));

    this.tree$ = this.documentService.tree;
    this.subscription = combineLatest(router$, this.tree$).subscribe(([router, tree]) => {
      const url = (router as NavigationEnd).url;
      const path = this.findPath(url);
      this.breadcrumbs = tree.getBreadcrumbs(path, new Breadcrumb('Documents', '/documents'));
      this.currentNode = tree.getNode(path);
      this.moveTargetCurrentNode = this.currentNode;
      this.form.get('parentId').setValue(this.currentNode.id);
    });
    this.reloadDocuments();
  }

  async ngOnInit(){
    this.updateHasFileTooLarge();
    this.updateHasFileExtensionNotSupported();
    this.loadHistoryDocumentSharing();

    try {
      this.data = await this.documentService.historyDocumentSharing();
      this.data = Deserialize(this.data, DocumentSharingHistory);
    } catch (error) {
      console.error('Error:', error);
    }

    const formControls = {
      id: [],
      parentId: [],
      type: [],
      nom: [this.DEFAULT_NOM]
    };
    for (const field of this.data) {
      const expirationDate = 'expirationDate_' + field.id;
      const allowAddDocuments = 'allowAddDocuments_' + field.id;
      formControls[expirationDate] = [formatDate(field.expirationDate, 'yyyy-MM-dd', 'en'), Validators.required];
      formControls[allowAddDocuments] = [field.allowAddDocuments];
    }
    this.formSharing = this.fb.group(formControls);
    this.documentService.getDocumentObligatoire().then((documents)=>{
      this.documentObligatoire = documents;
    })
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.cancelUpload();
  }

  goTo(node: Node) {
    if (node.type == NodeType.FOLDER) {
      this.router.navigateByUrl(`${this.router.url}/${node.slug}`);
    } else if (node.document && node.document.fichier) {
      const isInStandaloneMode = ('standalone' in window.navigator) && (window.navigator['standalone']);
      if (isInStandaloneMode) {
        this.download(node.document.fichier.url, node.document.nom);
      } else {
        //const safariWindow = window.open();
        this.documentService.download(node.document.fichier.id,node.document.fichier.token);
        //safariWindow.location.href = this.backResourceService.getUrl(node.document.fichier.url);
      }
    }
  }

  download(url, name) {
    fetch(url).then((response) => {
      return response.blob().then((b) => {
        const a = document.createElement('a');
        a.setAttribute('download', name);
        a.href = URL.createObjectURL(b);
        a.click();
      });
    });
  }

  addRoleProprietaire(node: Node) {
    const documentId = node.id;
    this.documentService.addRole(documentId, 'ROLE_PROPRIETAIRE', node.isDocumentCarnet, this.logementService.getLogementCourantId()).then(data => {
    });
    this.documentService.addRole(documentId, 'ROLE_COPROPRIETAIRE', node.isDocumentCarnet, this.logementService.getLogementCourantId()).then(data => {
      this.reloadDocuments();
      this.userService.load();
      this.toastService.show(data.message, {classname: 'toast-' + data.status});
    });
  }

  addRoleLocataire(node: Node) {
    const documentId = node.id;
    this.documentService.addRole(documentId, 'ROLE_LOCATAIRE', node.isDocumentCarnet, this.logementService.getLogementCourantId()).then(data => {
      this.reloadDocuments();
      this.userService.load();
      this.toastService.show(data.message, {classname: 'toast-' + data.status});
    });
  }

  removeRoleProprietaire(node: Node) {
    const documentId = node.id;
    this.documentService.removeRole(documentId, 'ROLE_PROPRIETAIRE', node.isDocumentCarnet, this.logementService.getLogementCourantId()).then(data => {
    });
    this.documentService.removeRole(documentId, 'ROLE_COPROPRIETAIRE', node.isDocumentCarnet, this.logementService.getLogementCourantId()).then(data => {
      this.reloadDocuments();
      this.userService.load();
      this.toastService.show(data.message, {classname: 'toast-' + data.status});
    });
  }

  removeRoleLocataire(node: Node) {
    const documentId = node.id;
    this.documentService.removeRole(documentId, 'ROLE_LOCATAIRE', node.isDocumentCarnet, this.logementService.getLogementCourantId()).then(data => {
      this.reloadDocuments();
      this.loadTree();
      this.userService.load();
      this.toastService.show(data.message, {classname: 'toast-' + data.status});
    });
  }

  navigateTarget(path: string) {
    this.tree$.pipe(first()).toPromise().then((tree: Tree) => {
      this.moveTargetCurrentNode = tree.getNode(path);
      this.form.get('parentId').setValue(this.moveTargetCurrentNode.id);
    });
  }

  navigateTargetParent(path: string) {
    this.navigateTarget(path.substring(0, path.lastIndexOf('/')));
  }

  isPossibleTarget(node: Node) {
    return node.writable && node.id != this.nodeId;
  }

  createOrRenameOrMoveNode() {

    const logementId = this.logementService.getLogementCourantId();
    const node = this.form.getRawValue();

    const promise = node.type == NodeType.FILE ?
      this.documentService.renameOrMoveDocument(logementId, node) :
      this.documentService.createOrRenameOrMoveFolder(logementId, node);

    promise.then(response => {
      this.toastService.show(response.message, {classname: 'toast-' + response.status});
      this.reloadDocuments();
    });
  }

  renameNodeResidenceLogement(){
    const node = this.form.getRawValue();
    const logementId = this.logementService.getLogementCourantId();

    const promise  = this.documentService.renameDocumentResidenceLogement(logementId, node);

    promise.then(response => {
      this.toastService.show(response.message, {classname: 'toast-' + response.status});
      this.reloadDocuments();
    });

  }

  deleteNode() {
    const node = this.form.getRawValue();

    const promise = node.type == NodeType.FOLDER ?
      this.documentService.deleteFolder(node) :
      this.documentService.deleteDocument(node);

    promise.then(response => {
      this.toastService.show(response.message, {classname: 'toast-' + response.status});
      this.reloadDocuments();
    });
  }

  deleteDocumentCarnet() {
    const node = this.form.getRawValue();

    const promise = this.documentService.deleteDocumentCarnet(node);

    promise.then(response => {
      this.toastService.show(response.message, {classname: 'toast-' + response.status});
      this.reloadDocuments();
    });
  }


  resetForm() {
    this.form.patchValue({
      id: null,
      parentId: this.currentNode.id,
      nom: this.DEFAULT_NOM,
      type: null
    });
  }

  populateForm(node: Node) {
    this.form.patchValue({
      id: node.id,
      nom: node.name,
      type: node.type
    });
  }

  openUploadModal() {
    this.updateHasFileTooLarge();
    this.updateHasFileExtensionNotSupported();
    const modal = this.modalService.open(this.uploadModal);
    modal.result.then(data => {
        this.cancelUpload();
      },
      reason => {
        this.cancelUpload();
      })
  }

  cancelUpload() {
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
    }
  }

  uploadFiles(modal: NgbModalRef) {
    this.uploadSubscription = this.documentService.uploadFiles(this.currentNode, this.filesData).subscribe(progress => {
      this.uploadProgress = progress;
    }, (response) => {
      this.toastService.showError(response.error.message);
      modal.close();
      this.reloadDocuments();
    }, () => {
      this.toastService.showSuccess('Vos fichier ont été correctement importés');
      modal.close();
      this.reloadDocuments();
    });
  }

  resetUpload() {
    this.uploadProgress = null;
    this.files = [];
  }

  getProgressPercent(item) {
    if (!this.uploadProgress || !this.uploadProgress[item.name]) {
      return 0;
    }
    const progress = this.uploadProgress[item.name];
    return progress.loaded / progress.total * 100;
  }

  get nodeId() {
    return this.form.get('id').value;
  }

  get nodeName() {
    return this.form.get('nom').value;
  }

  get nodeType() {
    return this.form.get('type').value;
  }

  private findPath(url) {
    return url.slice(url.split('/', 2).join('/').length + 1);
  }

  private reloadDocuments() {
    this.logement$.subscribe(logement => {
      this.documentService.load(logement.id).toPromise();
    });
  }

  canOpenToLocataire(child: Node) {
    let logementValue = null;
    this.logementService.logementCourant.subscribe(logement => {
      logementValue = logement;
    });

    if (child.isDocumentResidence && !logementValue.batiment.residence.bailleurFullResidence && this.userService.data.documentsEmis) {
      const userDocuments = this.userService.data.documentsEmis.filter(userDocument => {
        if (child.document && userDocument.document.id === child.document.id) {
          return true;
        }
      });

      return userDocuments.length === 0;
    } else {
      return child.canShare && child.document && !child.document.roles.includes('ROLE_LOCATAIRE');
    }
  }

  updateHasFileTooLarge() {
    this.hasFileTooLarge = this.files.filter(file => {
      return file.size > 31457280;
    }).length > 0;
  }

  updateHasFileExtensionNotSupported() {
    const ext = ['jpg', 'png', 'jpeg', 'jfif', 'gif', 'bmp', 'svg', 'txt', 'pdf', 'doc', 'docx', 'xml', 'xls', 'xlsx'];
    this.hasFileExtentionNotSupported = this.files.filter(file => {
      return !ext.includes(file.name.split('.').pop());
    }).length > 0;
  }

  getDocumentsObligatoires() {
    return this.documentObligatoire;
  }

  updateDocumentObligatoire(modal) {

    const target = this.moveTargetCurrentNodeFiltered;

    if (target?.isDocumentLogement) {

      const documentObligatoire = this.documentObligatoire.find(doc => doc.slug === target.slug);

      if (!documentObligatoire) {
        this.toastService.show("Document obligatoire introuvable", {classname: 'toast-error'});
        return;
      }

      this.documentService
        .setDocumentObligatoire(this.activeDocumentId, documentObligatoire.id)
        .then((response) => {

          this.reloadDocuments();
          this.toastService.show(response.message, {classname: 'toast-' + response.status});
          modal.close();

        });

      return;
    }

    //déplacement logement -> mes documents

    const payload = {
      source: 'logement',
      documentId: this.activeDocumentId,
      destinationType: 'mes_documents',
      destinationFolderId: target?.id
    };

    this.documentService.moveBetweenSpaces(payload).then((response: any) => {

      this.reloadDocuments();
      this.toastService.show(response.message, {classname: 'toast-' + response.status});
      modal.close();

    });

  }

  movePersonalDocument(modal) {

    const target = this.moveTargetCurrentNodeFiltered;

     //document personnel -> document personnel
    if (target?.path?.startsWith('mes-documents')) {

      const logementId = this.logementService.getLogementCourantId();

      const node = {
        id: this.activeDocumentId,
        parentId: target.id,
        type: NodeType.FILE,
        nom: this.nodeName
      };

      this.documentService.renameOrMoveDocument(logementId, node)
        .then((response) => {

          this.reloadDocuments();
          this.toastService.show(response.message, {classname: 'toast-' + response.status});
          modal.close();

        });

      return;
    }

    //document personnel -> logement
    const documentObligatoire = this.documentObligatoire.find(doc => doc.slug === target.slug);

    const payload = {
      source: 'mes_documents',
      documentId: this.activeDocumentId,
      destinationType: 'logement',
      documentObligatoireId: documentObligatoire?.id
    };

    this.documentService.moveBetweenSpaces(payload)
      .then((response:any) => {

        this.reloadDocuments();
        this.toastService.show(response.message, {classname: 'toast-' + response.status});
        modal.close();

      });

  }

  open(content: TemplateRef<any>) {
    this.modalService.open(content, { size: 'xl', scrollable: true});
  }
  allowDocumentControls(docuementSharingId) {
    return this.formSharing.get('allowAddDocuments_' + docuementSharingId) as UntypedFormControl;
  }

  expirationDateControls(docuementSharingId) {
    return this.formSharing.get('expirationDate_' + docuementSharingId) as UntypedFormControl;
  }

  deleteDocumentSharing(documentSharing: DocumentSharingHistory) {
    const promise = this.documentService.deleteDocumentSharing(documentSharing);

    promise.then(response => {
      this.toastService.show(response.message, {classname: 'toast-' + response.status});
      this.loadHistoryDocumentSharing();
    });
  }

  onSubmit(modal) {
    if (this.formSharing.valid) {
      const data = this.formSharing.getRawValue();
      this.documentService.updateDocumentSharings(data).then(response => {
        this.loadHistoryDocumentSharing();
        this.toastService.show(response.message, {classname: 'toast-' + response.status});
        if (response.status == 'error') {
          this.fh.setSymfonyFormErrors(this.form, response.errors, true);
          return;
        }
        modal.dismiss();
      });
    } else {
      this.toastService.showError('Une erreur est survenue lors de la modification des partages de documents');
    }
  }

  isProprietaire(logement) {
    return this.userService.isProprietaire(logement);
  }

  private loadHistoryDocumentSharing() {
    this.documentService.historyDocumentSharing().then(
      (response) => {
        this.documentSharings = Deserialize(response, DocumentSharingHistory);
        if (this.documentSharings.length == 0){
          this.modalService.dismissAll();
        }
      },
      (response) => {
        throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(response)}`)
      });
  }

  openMoveFromLogement(node: Node, modal) {

    this.activeDocumentId = node.id;
    this.sourceFolderId = this.currentNode?.id;

    this.form.patchValue({
      id: node.id,
      nom: node.name,
      type: node.type,
      parentId: this.currentNode?.id
    });

    this.tree$.pipe(first()).toPromise().then((tree: Tree) => {

      this.moveRootNodes = tree.root.children.filter(n =>
        n.slug === 'documents-logement' ||
        n.slug.startsWith('mes-documents-')
      );

      this.moveTargetCurrentNodeFiltered = this.currentNode;
      this.isMoveRootFiltered = false;

    });

    this.modalService.open(modal);
  }

  navigateTargetFiltered(path: string) {

    this.tree$.pipe(first()).toPromise().then((tree: Tree) => {

      const node = tree.getNode(path);

      if (!node) return;

      this.moveTargetCurrentNodeFiltered = node;
      this.isMoveRootFiltered = false;

    });

  }

  selectTargetFolder(node: Node) {

    // on mémorise le dossier choisi
    this.moveTargetCurrentNodeFiltered = node;

    // on continue la navigation si c'est un dossier
    if (node.type === NodeType.FOLDER) {
      this.navigateTargetFiltered(node.path);
    }

  }
  navigateTargetParentFiltered(path: string) {

    if (!path) {
      return;
    }

    const lastSlashIndex = path.lastIndexOf('/');

    // cas 1 : on est dans un sous dossier profond
    if (lastSlashIndex > -1) {

      const parentPath = path.substring(0, lastSlashIndex);
      this.navigateTargetFiltered(parentPath);
      return;

    }
    // cas 3 : retour à la racine (Logement / Mes documents)
    if (
      path.startsWith('documents-logement') ||
      path.startsWith('mes-documents')
    ) {

      this.isMoveRootFiltered = true;
      this.moveTargetCurrentNodeFiltered = null;

    }
    // cas 2 : on est dans un sous dossier direct du logement
    if (this.moveTargetCurrentNodeFiltered) {

      const parent = this.moveRootNodes.find(root =>
        root.children?.some(child => child.id === this.moveTargetCurrentNodeFiltered.id)
      );

      if (parent) {
        this.moveTargetCurrentNodeFiltered = parent;
        this.isMoveRootFiltered = false;
        return;
      }

    }
  }

  isSameFolder(node: Node): boolean {

    if (node.id && node.id === this.sourceFolderId) {
      return true;
    }

    if (node.slug === this.currentNode?.slug) {
      return true;
    }

    return false;
  }
  isValidMoveTarget(): boolean {

    const target = this.moveTargetCurrentNodeFiltered;

    if (!target) {
      return false;
    }

    return target.slug !== 'documents-logement';
  }
}
